import { Divider, Grid, Typography } from "@mui/material";
import { DataType, EffectSearch } from "components/EffectSearch/EffectSearch";
import { RequiredFieldLabel } from "components/FormControls/RequiredFieldLabel";
import { SelectInput } from "components/FormControls/Select/Select";
import { Toggle } from "components/FormControls/Toggle";
import { ReactComponent as PlusIcon } from "components/Icons/plus.svg";
import { variables } from "components/Layout/Achievement/variables";
import { prepareSchemaConditionForAutocompleteVariables } from "components/Layout/Campaign/Common/CampaignRules/rulesHelpers";
import { CommandBar } from "components/Tooltips/CommandBar/CommandBar";
import { useMessage } from "hooks/intl";
import { OLButton } from "OLButton";
import { useState } from "react";
import {
  AchievementAggregation,
  AchievementAggregationUnion,
  AchievementCompleteRulePeriod,
  AchievementRuleTypes,
  AchievementRuleTypeUnion,
  AchievementTriggers,
  AchievementTriggersUnion,
  CompleteRule,
} from "types/achievements";
import { CustomEventSchema } from "types/customEvents";
import { Consecutive } from "./Goals/Consecutive";
import { LastXDays, lastXDaysInitalValues } from "./Goals/LastXDays";
import { Overall, overallInitalValues } from "./Goals/Overall";
import { useStyles } from "./RuleGoal.styles";
import { RuleGoalReadOnly } from "./RuleGoalReadOnly";
import { limitAchievementsArray } from "./utils";

const streakTypes = [
  {
    label: "dailyStreak",
    value: "day",
  },
  {
    label: "weeklyStreak",
    value: "week",
  },
  {
    label: "monthlyStreak",
    value: "month",
  },
  {
    label: "yearlyStreak",
    value: "year",
  },
];

const StreakType = ({
  goalFieldName,
  setFieldValue,
}: {
  goalFieldName: string;
  setFieldValue: any;
}) => {
  const { message } = useMessage();

  return (
    <SelectInput
      width="100%"
      name={`${goalFieldName}.period.streakType`}
      items={streakTypes.map((option) => ({
        id: option.value,
        label: message({ id: option.label }),
      }))}
      label={<RequiredFieldLabel text={message({ id: "streak" })} />}
      data-test-id="rule-goal-streak-select"
      onChange={(e) =>
        setFieldValue(`${goalFieldName}.period.streakType`, e.target.value)
      }
    />
  );
};

interface Props {
  aggregation: {
    type: AchievementAggregationUnion;
    rule?: string;
  };
  setFieldValue: (field: string, value: any) => void;
  activeRule: number;
  trigger: AchievementTriggersUnion;
  completeRule: CompleteRule & {
    draft?: CompleteRule;
    isEditing?: boolean;
  };
  conditions: any[];
  type: AchievementRuleTypeUnion;
  event?: string;
  errors: any;
  schemaDetails?: CustomEventSchema;
}
export const RuleGoal = ({
  aggregation,
  activeRule,
  completeRule,
  trigger,
  conditions,
  type,
  event,
  errors,
  schemaDetails,
  setFieldValue,
}: Props) => {
  const classes = useStyles();
  const { message } = useMessage();

  const [isCommandBarOpen, setIsCommandBarOpen] = useState(false);

  const { REFERRAL } = AchievementRuleTypes;
  const { TRANSACTION, CUSTOM_EVENT } = AchievementTriggers;
  const { QUANTITY, EXPRESSION } = AchievementAggregation;
  const { OVERALL, LAST_DAY, DAY, WEEK, MONTH, YEAR, STREAK } =
    AchievementCompleteRulePeriod;

  const isAggregationExpressionType = aggregation?.type === EXPRESSION;

  const isUniqueRefereeInfoVisible =
    type === REFERRAL &&
    trigger === TRANSACTION &&
    aggregation.type === QUANTITY;

  const isUniqueCustomEventAttributesVisible = trigger === CUSTOM_EVENT;

  const goalFieldName = completeRule.isEditing
    ? `rules[${activeRule}].completeRule.draft`
    : `rules[${activeRule}].completeRule`;

  const completeRuleData = completeRule.isEditing
    ? completeRule?.draft
    : completeRule;

  const isConsecutiveType =
    completeRuleData?.period?.type !== OVERALL &&
    completeRuleData?.period?.type !== LAST_DAY;

  const isSaveBtnDisabled =
    (isConsecutiveType && !completeRuleData?.period?.streakType) ||
    (isConsecutiveType && isAggregationExpressionType && !aggregation.rule);

  const isOverallType = completeRuleData?.period?.type === OVERALL;

  const isSetGoalBtnDisabled =
    !!completeRule.period?.type && !completeRule.isEditing;

  const isReadOnlyGoalVisible =
    completeRule?.period?.type && !completeRule.isEditing;

  const isRuleGoalNotSaved =
    !!errors?.completeRule?.isEditing && completeRule.isEditing;

  const variablesData: DataType[] = [
    {
      name: "variables",
      items:
        trigger === TRANSACTION
          ? variables
          : prepareSchemaConditionForAutocompleteVariables(
              conditions,
              "number",
            ),
    },
  ];

  const goals = [
    {
      component: Overall,
      initialValues: overallInitalValues,
      label: "overall",
    },
    {
      component: LastXDays,
      initialValues: lastXDaysInitalValues,
      label: "lastxDays",
    },
    {
      component: (
        <StreakType
          goalFieldName={goalFieldName}
          setFieldValue={setFieldValue}
        />
      ),
      initialValues: {
        period: { type: STREAK, consecutive: "" },
        periodGoal: "",
      },
      label: "streak",
    },
  ];

  const getGoalComponent = (completeRule: CompleteRule) => {
    if (!completeRuleData) {
      return;
    }

    switch (true) {
      case completeRule.period?.type === OVERALL:
        return (
          <Overall
            errors={errors}
            activeRule={activeRule}
            fieldName={goalFieldName}
            completeRule={completeRuleData}
          />
        );
      case completeRule.period?.type === LAST_DAY:
        return (
          <LastXDays
            errors={errors}
            activeRule={activeRule}
            fieldName={goalFieldName}
            completeRule={completeRuleData}
          />
        );
      case completeRule.period?.streakType === DAY:
      case completeRule.period?.streakType === WEEK:
      case completeRule.period?.streakType === MONTH:
      case completeRule.period?.streakType === YEAR:
        return (
          <Consecutive
            errors={errors}
            activeRule={activeRule}
            fieldName={goalFieldName}
            completeRule={completeRuleData}
            streakType={completeRule.period?.streakType}
            trigger={trigger}
            aggregation={aggregation}
            event={event}
          />
        );
    }
  };

  const goalComponent = completeRuleData && getGoalComponent(completeRuleData);

  return (
    <>
      <div className={classes.wrapper}>
        <div className={classes.wrapperTitle}>
          <Typography>{message({ id: "ruleGoal" })}</Typography>
        </div>
        <Grid container padding={8}>
          <Grid item xs={12} className={classes.setGoalWrapper}>
            <Typography className={classes.wrapperSubtitle}>
              {message({ id: "goal" })}
            </Typography>
            <OLButton
              size="small"
              variant="outlined"
              data-test-id="set-goal"
              startIcon={<PlusIcon />}
              className={classes.setGoalButton}
              disabled={isSetGoalBtnDisabled}
              onClick={() => setIsCommandBarOpen(true)}
            >
              {message({ id: "setGoal" })}
            </OLButton>
          </Grid>
          {isRuleGoalNotSaved && (
            <Typography className={classes.ruleGoalWarning}>
              {message({ id: "notSavedGoalWarning" })}
            </Typography>
          )}
        </Grid>
        {completeRule.isEditing && (
          <section className={classes.goalSection}>
            <Typography className={classes.wrapperSubtitle}>
              {message({ id: "newGoal" })}
            </Typography>
            <Grid container pt={12} spacing={12}>
              {isAggregationExpressionType && (
                <Grid item xs={12}>
                  <Typography className={classes.sectionSubTitle}>
                    {message({
                      id:
                        trigger === AchievementTriggers.TRANSACTION
                          ? "transactionTargetAttribute"
                          : "chooseEventTargetAttribute",
                    })}
                  </Typography>
                  <EffectSearch
                    items={variablesData}
                    data-test-id={`rule-${activeRule}-goal-rule`}
                    name={`rules[${activeRule}].aggregation.rule`}
                    placeholder={message({ id: "autocompletePlaceholder" })}
                  />
                  <Divider className={classes.divider} />
                </Grid>
              )}

              <Grid item xs={isConsecutiveType || isOverallType ? 6 : 12}>
                <SelectInput
                  width="100%"
                  name={`${goalFieldName}.period.type`}
                  items={limitAchievementsArray.map((option) => ({
                    id: option.value,
                    label: message({ id: option.label }),
                  }))}
                  label={
                    <RequiredFieldLabel text={message({ id: "occurrence" })} />
                  }
                  data-test-id="rule-goal-occurence-select"
                />
              </Grid>
              {completeRuleData?.period?.type === STREAK && (
                <Grid item xs={6}>
                  <StreakType
                    goalFieldName={goalFieldName}
                    setFieldValue={setFieldValue}
                  />
                </Grid>
              )}
              {goalComponent}
              {isUniqueRefereeInfoVisible && (
                <Grid xs={12} item display="flex" alignItems="center">
                  <Toggle
                    className={classes.toggleLabel}
                    name={`rules[${activeRule}].uniqueReferee`}
                    helperText={message({ id: "uniqueRefereeHelperText" })}
                    label={message(
                      {
                        id: "uniqueRefereeInfo",
                      },
                      {
                        span: (chunks: React.ReactNode[]) => (
                          <span className={classes.bolded}>{chunks}</span>
                        ),
                      },
                    )}
                  />
                </Grid>
              )}
              {isUniqueCustomEventAttributesVisible && (
                <>
                  <Grid xs={12} item display="flex" alignItems="center">
                    <Toggle
                      data-test-id="unique-value-toggl"
                      className={classes.toggleLabel}
                      name={`${goalFieldName}.onlyUniqueAttributeValues`}
                      helperText={message({
                        id: "uniqueCustomEventAttributesHelperText",
                      })}
                      label={message(
                        {
                          id: "uniqueCustomEventAttributesInfo",
                        },
                        {
                          span: (chunks: React.ReactNode[]) => (
                            <span className={classes.bolded}>{chunks}</span>
                          ),
                        },
                      )}
                    />
                  </Grid>
                  {!!completeRuleData?.onlyUniqueAttributeValues && (
                    <Grid xs={12} item>
                      <SelectInput
                        width="100%"
                        name={`${goalFieldName}.uniqueAttribute`}
                        items={
                          schemaDetails
                            ? schemaDetails.schema?.fields?.map((option) => ({
                                id: option.name,
                                label: message({ id: option.name }),
                              }))
                            : []
                        }
                        label={
                          <RequiredFieldLabel
                            text={message({ id: "customEventAttribute" })}
                          />
                        }
                        data-test-id="custom-event-attribute"
                        error={errors?.completeRule?.uniqueAttribute}
                      />
                    </Grid>
                  )}
                </>
              )}
            </Grid>
            <Grid container className={classes.goalButtons}>
              <OLButton
                size="small"
                onClick={() => {
                  setFieldValue(`rules[${activeRule}].completeRule`, {
                    draft: undefined,
                    isEditing: false,
                    period: completeRule.period,
                    periodGoal: completeRule.periodGoal,
                    onlyUniqueAttributeValues:
                      completeRule.onlyUniqueAttributeValues,
                    uniqueAttribute: completeRule.uniqueAttribute,
                  });
                }}
                data-test-id="rule-goal-cancel"
              >
                {message({ id: "close" })}
              </OLButton>
              <OLButton
                size="small"
                variant="contained"
                disabled={isSaveBtnDisabled}
                onClick={() => {
                  setFieldValue(`rules[${activeRule}].completeRule`, {
                    isEditing: false,
                    ...completeRuleData,
                  });
                }}
                data-test-id="rule-goal-save"
              >
                {message({ id: "saveGoal" })}
              </OLButton>
            </Grid>
          </section>
        )}
        {isReadOnlyGoalVisible && (
          <RuleGoalReadOnly
            completeRule={completeRule}
            setFieldValue={setFieldValue}
            fieldName={goalFieldName}
            aggregation={aggregation}
            trigger={trigger}
            event={event}
            errors={errors}
          />
        )}
      </div>
      <CommandBar
        conditions={goals}
        isOpen={isCommandBarOpen}
        onClose={() => setIsCommandBarOpen(false)}
        onItemClick={(item: any) => {
          setFieldValue(`rules[${activeRule}].completeRule`, {
            draft: item.initialValues,
            isEditing: true,
          });
        }}
        placeholder="searchForGoals"
        hasCategories={false}
        data-test-id="set-goal-occurrence"
      />
    </>
  );
};
